import React, { useState } from 'react'
import { useInvoice } from '@/contexts/InvoiceContext'
import { EmailTemplate } from '@/types/invoice'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'

import { Settings, Mail } from 'lucide-react'

interface EmailTemplateEditorProps {
  onPreview: (template: EmailTemplate) => void
  onOpenEmail: (template: EmailTemplate) => void
}

const VARIABLES = [
  { key: '{client}', description: 'Client name' },
  { key: '{number}', description: 'Invoice number' },
  { key: '{amount}', description: 'Invoice amount with currency' },
  { key: '{dueDate}', description: 'Due date' },
  { key: '{daysOverdue}', description: 'Days overdue (if applicable)' },
  { key: '{description}', description: 'Invoice description' }
]

export function EmailTemplateEditor({ onPreview, onOpenEmail }: EmailTemplateEditorProps) {
  const { state, updateEmailTemplate } = useInvoice()
  const [isOpen, setIsOpen] = useState(false)
  const [localTemplate, setLocalTemplate] = useState<EmailTemplate>(state.emailTemplate)

  const handleSave = () => {
    updateEmailTemplate(localTemplate)
    setIsOpen(false)
  }

  const handleCancel = () => {
    setLocalTemplate(state.emailTemplate)
    setIsOpen(false)
  }

  const insertVariable = (variable: string) => {
    const textarea = document.getElementById('email-body') as HTMLTextAreaElement
    if (textarea) {
      const start = textarea.selectionStart
      const end = textarea.selectionEnd
      const text = localTemplate.body
      const newText = text.substring(0, start) + variable + text.substring(end)
      setLocalTemplate(prev => ({ ...prev, body: newText }))
      
      // Set cursor position after the inserted variable
      setTimeout(() => {
        textarea.focus()
        textarea.setSelectionRange(start + variable.length, start + variable.length)
      }, 0)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Email Template</h3>
        <div className="flex gap-2">
          <Button
            onClick={() => onPreview(localTemplate)}
            variant="outline"
            size="sm"
            className="flex items-center gap-2"
          >
            <Mail className="w-4 h-4" />
            Preview
          </Button>
          <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" className="flex items-center gap-2">
                <Settings className="w-4 h-4" />
                Edit Template
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Edit Email Template</DialogTitle>
              </DialogHeader>
              
              <div className="space-y-6">
                {/* Template Name */}
                <div>
                  <label className="block text-sm font-medium mb-2">Template Name</label>
                  <input
                    type="text"
                    value={localTemplate.name}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLocalTemplate(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Template name"
                    className="w-full p-3 border border-gray-300 rounded-md"
                  />
                </div>

                {/* Subject */}
                <div>
                  <label className="block text-sm font-medium mb-2">Subject</label>
                  <input
                    type="text"
                    value={localTemplate.subject}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLocalTemplate(prev => ({ ...prev, subject: e.target.value }))}
                    placeholder="Email subject"
                    className="w-full p-3 border border-gray-300 rounded-md"
                  />
                </div>

                {/* Variables */}
                <div>
                  <label className="block text-sm font-medium mb-2">Available Variables</label>
                  <div className="grid grid-cols-2 gap-2">
                    {VARIABLES.map((variable) => (
                      <Button
                        key={variable.key}
                        onClick={() => insertVariable(variable.key)}
                        variant="outline"
                        size="sm"
                        className="justify-start text-left"
                      >
                        <code className="text-xs bg-gray-100 px-1 rounded mr-2">{variable.key}</code>
                        {variable.description}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Body */}
                <div>
                  <label className="block text-sm font-medium mb-2">Email Body</label>
                  <textarea
                    id="email-body"
                    value={localTemplate.body}
                    onChange={(e) => setLocalTemplate(prev => ({ ...prev, body: e.target.value }))}
                    className="w-full h-64 p-3 border border-gray-300 rounded-md resize-none font-mono text-sm"
                    placeholder="Email body content..."
                  />
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-2">
                  <Button onClick={handleCancel} variant="outline">
                    Cancel
                  </Button>
                  <Button onClick={handleSave}>
                    Save Template
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Current Template Preview */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <div className="text-sm text-gray-600 mb-2">
          <strong>Subject:</strong> {localTemplate.subject}
        </div>
        <div className="text-sm text-gray-600">
          <strong>Body Preview:</strong>
          <div className="mt-1 whitespace-pre-wrap text-xs bg-white p-2 rounded border">
            {localTemplate.body.substring(0, 200)}...
          </div>
        </div>
      </div>
    </div>
  )
} 